import dayjs from "dayjs";
import html2pdf from "html2pdf.js";
import { applyStandardPdfStyles } from "./pdfUtils";

/**
 * Generates and downloads a Gate Pass PDF.
 * 
 * @param {Object} options - Configuration options
 * @param {Object} options.gatePass - The gate pass data object
 * @param {Function} options.translate - Translation function
 * @param {Object} options.assets - Required images (skyautologo, statusPng)
 * @param {Object} options.status - Status object with text and color
 * @param {string} options.outputMode - 'save' (default) or 'view'
 */
export const generateGatePassPdf = ({ gatePass, translate, assets, status, outputMode = 'save' }) => {
  if (!gatePass) return;

  const { skyautologo, statusPng } = assets;

  const generatedDate = dayjs().format("MMMM D, YYYY hh:mm A");
  const validTill = dayjs(gatePass.validTillDate).format("MMMM D, YYYY");
  const createdAt = dayjs(gatePass.createdAt).format("MMMM D, YYYY hh:mm A");

  const buyer = gatePass.buyer?.user || {};
  const vehicle = gatePass.vehicle || {};
  const invoice = gatePass.buyerInvoice || {};

  // Robust extraction of vehicle details
  const vehicleMake = vehicle.MakeModel?.make?.name || vehicle.make || vehicle.name?.split(" ")[1] || "N/A";
  const vehicleModel = vehicle.MakeModel?.name || vehicle.model || vehicle.name?.split(" ")[2] || "N/A";
  const vehicleYear = vehicle.year || vehicle.name?.split(" ")[0] || "N/A";
  const vehicleColor = vehicle.color || "N/A";

  // Extraction of variant - usually the rest of the name after year, make, model
  const nameParts = vehicle.name?.split(" ") || [];
  const vehicleVariant = nameParts.slice(3).join(" ") || "N/A";

  const htmlContent = `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  body {
    font-family: 'Segoe UI', Arial, sans-serif;
    margin: 25px;
    color: #2d3436;
    background: #fff;
  }

  .expired-overlay {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 250px;
    height: auto;
    opacity: 0.7;
    z-index: 9999;
    pointer-events: none;
  }

  .container {
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    padding: 20px;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 3px solid #135EA0;
    padding-bottom: 12px;
    margin-bottom: 20px;
  }

  .header img {
    height: 55px;
  }

  .title {
    font-size: 22px;
    font-weight: 700;
    color: #135EA0;
    letter-spacing: 1px;
  }

  .top-info {
    display: flex;
    justify-content: space-between;
    margin-bottom: 15px;
  }

  .gate-box p {
    margin: 2px 0;
    font-size: 13px;
  }

  .gate-number {
    font-size: 18px;
    font-weight: bold;
    color: #135EA0;
  }

  .vehicle-title {
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    margin: 15px 0;
    color: #222;
  }

  .card {
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 12px 15px;
    margin-bottom: 12px;
  }

  .card h3 {
    font-size: 14px;
    margin-bottom: 8px;
    color: #135EA0;
  }

  .card p {
    font-size: 13px;
    margin: 3px 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 5px;
  }

  table td {
    padding: 6px 4px;
    font-size: 13px;
    border-bottom: 1px solid #eee;
  }

  table td:first-child {
    font-weight: 600;
    color: #555;
    width: 40%;
  }

  .footer {
    text-align: center;
    font-size: 12px;
    margin-top: 15px;
    border-top: 1px solid #ddd;
    padding-top: 8px;
  }

  .signature {
    display: flex;
    justify-content: space-between;
    margin-top: 25px;
    text-align: center;
    font-size: 12px;
  }

  .sign-box {
    width: 40%;
  }

  .line {
    border-top: 1px solid #000;
    margin-top: 30px;
  }

  .note {
    margin-top: 15px;
    font-size: 12px;
    font-style: italic;
    text-align: center;
  }
</style>
</head>
<body>
<img  src="${statusPng}" 
     alt="Expired" 
     class="expired-overlay" />
<div class="container">
  <div class="header">
    <img src="${skyautologo}" />
    <div class="title">GATE PASS</div>
  </div>

  <div class="top-info">
    <div class="gate-box">
      <p>GATE PASS NO</p>
      <p class="gate-number">${gatePass.gatePassNumber || gatePass.id}</p>
    </div>
    <div class="gate-box">
      <p><strong>Date:</strong> ${createdAt}</p>
      <p><strong>Valid Till:</strong> ${validTill}</p>
    </div>
  </div>

  <div class="vehicle-title">
    ${vehicle.name || "N/A"}
  </div>

  <div class="card">
    <h3>Buyer Information</h3>
    <p><strong>Name:</strong> ${buyer.name || "N/A"} - ${buyer.clientId || ""}</p>
    <p><strong>Email:</strong> ${buyer.email || "N/A"}</p>
    <p><strong>Phone:</strong> ${buyer.phone || buyer.mobileNo || "N/A"}</p>
    <p><strong>Hand Over To:</strong> Buyer</p>
  </div>

  <div class="card">
    <h3>Vehicle Information</h3>
    <table>
      <tr><td>Invoice No</td><td>${invoice.id || gatePass.buyerInvoiceId || "N/A"}</td></tr>
      <tr><td>VIN No</td><td>${vehicle.vinNumber ? vehicle.vinNumber.slice(0, 11) + "******" : "N/A"}</td></tr>
      <tr><td>Lot No</td><td>${vehicle.lotNumber || "N/A"}</td></tr>
      <tr><td>Make</td><td>${vehicleMake}</td></tr>
      <tr><td>Model</td><td>${vehicleModel}</td></tr>
      <tr><td>Variant</td><td>${vehicleVariant}</td></tr>
      <tr><td>Year</td><td>${vehicleYear}</td></tr>
      <tr><td>Color</td><td>${vehicleColor}</td></tr>
      <tr><td>Sold Status</td><td>${vehicle.soldStatus || "N/A"}</td></tr>
      <tr><td>Payment</td><td>${invoice.remainingAmount === 0 ? "PAID" : "UNPAID"}</td></tr>
    </table>
  </div>

  <div class="footer">
    <p><strong>Sky Auto Auction</strong></p>
    <p>+971 50 222 0000</p>
    <p>skyautotrade.aws@gmail.com | www.skyautoauction.com</p>
  </div>

  <div class="signature">
    <div class="sign-box">
      <div class="line"></div>
      Authorized Person<br/>Stamp / Seal
    </div>
    <div class="sign-box">
      <div class="line"></div>
      Receiver
    </div>
  </div>

  <div class="note">
    NOTE: Please ensure you bring a printed (hard copy) of your gate pass.
  </div>
</div>
</body>
</html>
`;

  const element = document.createElement("div");
  element.innerHTML = htmlContent;

  const options = {
    margin: [10, 10, 10, 10],
    filename: `GatePass-${gatePass.gatePassNumber || gatePass.id}.pdf`,
    image: { type: "jpeg", quality: 1 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
  };

  if (outputMode === 'view') {
    html2pdf().set(options).from(element).toPdf().get("pdf").then((pdf) => {
        applyStandardPdfStyles(pdf, generatedDate);
        const url = pdf.output('bloburl');
        window.open(url, '_blank');
    });
  } else {
    html2pdf().set(options).from(element).toPdf().get("pdf").then((pdf) => {
        applyStandardPdfStyles(pdf, generatedDate);
    }).save();
  }
};
